import { applySeo } from "../lib/seo";
import { useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PROJECTS, PROJECTS_PAGE as PP } from "../data/site";
import { MaskedLines, Reveal, SectionHeading } from "../components/site/Reveal";
import { CTABand } from "../components/site/CTABand";

export default function Projects() {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 600], [0, 120]);
  const [projects, setProjects] = useState(PROJECTS);

  useEffect(() => {
    applySeo({ paths: ["/projects/"], fallbackTitle: "Project Portfolio | Cedar Views, Surrey & Hampshire" });
    window.scrollTo(0, 0);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/case-studies`)
      .then((r) => r.json())
      .then((d) => { if (Array.isArray(d) && d.length) setProjects(d); })
      .catch(() => {});
  }, []);

  const gallery = PP.gallery;

  return (
    <main data-testid="projects-page">
      <section className="relative h-[70vh] min-h-[520px] overflow-hidden flex items-end">
        <motion.div style={{ y }} className="absolute inset-0">
          <img src={PP.heroImage} alt="Cedar Views project in Kingswood Surrey" className="w-full h-[120%] object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050A15] via-[#050A15]/45 to-[#050A15]/25" />
        </motion.div>
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pb-16 w-full">
          <Reveal delay={0.1}><p className="overline-x mb-5">{PP.overline}</p></Reveal>
          <MaskedLines as="h1" lines={[PP.title]} delay={0.2} className="font-display tracking-tighter text-4xl sm:text-5xl lg:text-7xl text-white" />
          <Reveal delay={0.5} className="mt-6">
            <h2 className="text-base md:text-lg text-white/70 max-w-xl font-normal">
              {PP.sub}
            </h2>
          </Reveal>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <SectionHeading number="01" kicker="Case studies" title="Recent installations." />
        <div className="space-y-24">
          {projects.map((p, i) => (
            <Reveal key={p.id || p.title}>
              <article data-testid={`project-article-${i}`} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 items-center`}>
                <div className={`img-hover aspect-[16/11] lg:col-span-7 ${i % 2 ? "lg:order-2 lg:col-start-6" : ""}`}>
                  <img src={p.image} alt={p.title} loading="lazy" className="w-full h-full object-cover" />
                </div>
                <div className={`lg:col-span-4 ${i % 2 ? "lg:order-1 lg:col-start-1" : "lg:col-start-9"}`}>
                  <span className="font-mono-x text-xs text-[#3C64A8]">{String(i + 1).padStart(2, "0")}</span>
                  <p className="font-mono-x text-[10px] tracking-[0.25em] uppercase text-white/40 mt-4">{p.location}</p>
                  <h3 className="font-display font-medium text-2xl lg:text-3xl text-white mt-3 tracking-tight">{p.title}</h3>
                  <p className="text-sm text-white/45 mt-3 font-mono-x">{p.spec}</p>
                  <p className="text-white/60 leading-relaxed mt-5">{p.body}</p>
                  {p.has_full && p.slug && (
                    <Link to={`/projects/${p.slug}/`} data-testid={`project-full-link-${i}`} className="btn-ghost mt-7 inline-flex">
                      Read the full project <ArrowRight size={14} />
                    </Link>
                  )}
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t hairline bg-[#070E1D]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <SectionHeading number="02" kicker={PP.galleryKicker} title={PP.galleryTitle} />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {gallery.map((src, i) => (
              <Reveal key={src} delay={(i % 4) * 0.06} className={i % 7 === 0 ? "col-span-2 row-span-2" : ""}>
                <div className="img-hover h-full">
                  <img src={src} alt={`Cedar Views installation detail ${i + 1}`} loading="lazy" className="w-full h-full object-cover aspect-square" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTABand />
    </main>
  );
}
